import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { AdminShell } from "@/components/AdminShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  adminCreateTestOrder,
  adminGetOrderSettings,
  adminListOrders,
  adminOrderStats,
  adminUpdateOrderSettings,
} from "@/lib/orders-admin.functions";
import {
  DELIVERY_METHODS,
  ORDER_STATUSES,
  deliveryLabel,
  kr,
  statusLabel,
  statusTone,
} from "@/lib/orders";

export const Route = createFileRoute("/_authenticated/admin/ordrar")({
  component: AdminOrders,
  head: () => ({
    meta: [{ title: "Ordrar – administration" }, { name: "robots", content: "noindex" }],
  }),
});

function AdminOrders() {
  const list = useServerFn(adminListOrders);
  const stats = useServerFn(adminOrderStats);
  const getSettings = useServerFn(adminGetOrderSettings);
  const saveSettings = useServerFn(adminUpdateOrderSettings);
  const testOrder = useServerFn(adminCreateTestOrder);
  const qc = useQueryClient();

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [method, setMethod] = useState("all");
  const [mode, setMode] = useState<"all" | "simulated" | "live">("all");
  const [testSlug, setTestSlug] = useState("");
  const [testMethod, setTestMethod] = useState("parcel_shop");
  const [testTarget, setTestTarget] = useState("paid");

  const statsQ = useQuery({ queryKey: ["admin-order-stats"], queryFn: () => stats({}) });
  const settingsQ = useQuery({ queryKey: ["admin-order-settings"], queryFn: () => getSettings({}) });
  const rowsQ = useQuery({
    queryKey: ["admin-orders", search, status, method, mode],
    queryFn: () => list({ data: { search, status, method, mode, limit: 50 } }),
  });

  const settings = settingsQ.data;

  const saveMut = useMutation({
    mutationFn: (patch: { simulationMode?: boolean; feePercent?: number; approvalDays?: number }) =>
      saveSettings({
        data: {
          simulationMode: patch.simulationMode ?? settings?.simulationMode ?? true,
          feePercent: patch.feePercent ?? settings?.feePercent ?? 5,
          approvalDays: patch.approvalDays ?? settings?.approvalDays ?? 3,
        },
      }),
    onSuccess: () => {
      toast.success("Inställningarna är sparade.");
      qc.invalidateQueries({ queryKey: ["admin-order-settings"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const testMut = useMutation({
    mutationFn: () =>
      testOrder({
        data: { slug: testSlug.trim(), method: testMethod as any, advanceTo: testTarget as any },
      }),
    onSuccess: () => {
      toast.success("Testordern är skapad.");
      qc.invalidateQueries({ queryKey: ["admin-orders"] });
      qc.invalidateQueries({ queryKey: ["admin-order-stats"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const s = statsQ.data;

  return (
    <AdminShell title="Ordrar">
      <h1 className="font-heading text-2xl font-bold text-foreground">Trygg affär – ordrar</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        Alla köp, frakter och utbetalningar. I övningsläge simuleras betalning och fraktsedlar.
      </p>

      <div className="mt-6 grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {[
          ["Ordrar", s ? String(s.total) : "–"],
          ["Pågående", s ? String(s.open) : "–"],
          ["Klara", s ? String(s.released) : "–"],
          ["Tvister", s ? String(s.disputed) : "–"],
          ["Omsättning", s ? kr(s.revenue) : "–"],
          ["Avgifter", s ? kr(s.fees) : "–"],
        ].map(([label, value]) => (
          <div key={label} className="rounded-lg border border-border bg-card p-3">
            <p className="text-xs text-muted-foreground">{label}</p>
            <p className="mt-1 font-heading text-lg font-semibold">{value}</p>
          </div>
        ))}
      </div>

      {/* Inställningar */}
      <section className="mt-8 rounded-xl border border-border bg-card p-4">
        <h2 className="font-heading text-lg font-semibold">Inställningar</h2>
        <div className="mt-3 grid gap-4 sm:grid-cols-3">
          <label className="flex items-center gap-3 text-sm">
            <Switch
              checked={settings?.simulationMode ?? true}
              onCheckedChange={(v) => saveMut.mutate({ simulationMode: v })}
            />
            <span>
              Övningsläge
              <span className="block text-xs text-muted-foreground">
                {settings?.liveKeys ? "Skarpa nycklar finns" : "Skarpt läge kräver nycklar"}
              </span>
            </span>
          </label>
          <label className="text-sm">
            Serviceavgift (%)
            <Input
              type="number"
              min={0}
              max={30}
              defaultValue={settings?.feePercent ?? 5}
              onBlur={(e) => saveMut.mutate({ feePercent: Number(e.target.value) })}
            />
          </label>
          <label className="text-sm">
            Godkännandefrist (dagar)
            <Input
              type="number"
              min={1}
              max={30}
              defaultValue={settings?.approvalDays ?? 3}
              onBlur={(e) => saveMut.mutate({ approvalDays: Number(e.target.value) })}
            />
          </label>
        </div>
      </section>

      {/* Testorder */}
      <section className="mt-6 rounded-xl border border-dashed border-border bg-muted/40 p-4">
        <h2 className="font-heading text-lg font-semibold">Skapa testorder</h2>
        <div className="mt-3 grid gap-3 sm:grid-cols-4">
          <Input
            placeholder="annons-slug"
            value={testSlug}
            onChange={(e) => setTestSlug(e.target.value)}
          />
          <Select value={testMethod} onValueChange={setTestMethod}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DELIVERY_METHODS.map((m) => (
                <SelectItem key={m} value={m}>
                  {deliveryLabel(m)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={testTarget} onValueChange={setTestTarget}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pending_payment">Stanna vid obetald</SelectItem>
              <SelectItem value="paid">Betald</SelectItem>
              <SelectItem value="in_transit">På väg</SelectItem>
              <SelectItem value="delivered">Levererad</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={() => testMut.mutate()} disabled={testMut.isPending || !testSlug.trim()}>
            Skapa testorder
          </Button>
        </div>
      </section>

      {/* Filter */}
      <div className="mt-8 grid gap-3 sm:grid-cols-4">
        <Input
          placeholder="Sök titel, köpare eller kolli-id"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alla statusar</SelectItem>
            {ORDER_STATUSES.map((st) => (
              <SelectItem key={st} value={st}>
                {statusLabel(st)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={method} onValueChange={setMethod}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alla leveranssätt</SelectItem>
            {DELIVERY_METHODS.map((m) => (
              <SelectItem key={m} value={m}>
                {deliveryLabel(m)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={mode} onValueChange={(v) => setMode(v as typeof mode)}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Övning och skarpt</SelectItem>
            <SelectItem value="simulated">Bara övning</SelectItem>
            <SelectItem value="live">Bara skarpt</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="mt-4 overflow-x-auto rounded-xl border border-border">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="bg-muted/60 text-left">
            <tr>
              <th className="px-3 py-2">Order</th>
              <th className="px-3 py-2">Pryl</th>
              <th className="px-3 py-2">Leverans</th>
              <th className="px-3 py-2">Belopp</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Skapad</th>
            </tr>
          </thead>
          <tbody>
            {(rowsQ.data?.rows ?? []).map((r: any) => (
              <tr key={r.id} className="border-t border-border hover:bg-muted/40">
                <td className="px-3 py-2 font-mono">
                  <Link to="/admin/ordrar/$id" params={{ id: r.id }} className="text-primary">
                    #{r.order_no}
                  </Link>
                  {r.is_simulated && <span className="ml-2 text-xs text-amber-600">övning</span>}
                </td>
                <td className="max-w-[220px] truncate px-3 py-2">{r.ad_title}</td>
                <td className="px-3 py-2">{deliveryLabel(r.delivery_method)}</td>
                <td className="px-3 py-2">{kr(r.total_amount)}</td>
                <td className="px-3 py-2">
                  <span className={`rounded-full px-2 py-0.5 text-xs ${statusTone(r.status)}`}>
                    {statusLabel(r.status)}
                  </span>
                </td>
                <td className="px-3 py-2 text-muted-foreground">
                  {new Date(r.created_at).toLocaleDateString("sv-SE")}
                </td>
              </tr>
            ))}
            {!rowsQ.isLoading && (rowsQ.data?.rows.length ?? 0) === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">
                  Inga ordrar matchar filtret.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </AdminShell>
  );
}
